"use client";

import { useState } from "react";
import Link from "next/link";
import { BookOpen } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type AuthorizationScheme = {
  key: string;
  type: string;
  scheme?: string;
  bearerFormat?: string;
  name?: string;
  in?: string;
  description?: string;
};

export function ApiAuthorization({
  operation,
  schemes,
}: {
  operation: string;
  schemes: AuthorizationScheme[];
}) {
  const [selected, setSelected] = useState(schemes[0]?.key ?? "");
  const scheme = schemes.find((item) => item.key === selected) ?? schemes[0];
  if (!scheme) return null;
  const bearer = scheme.type === "http" && scheme.scheme === "bearer";
  return (
    <section
      className="api-detail-section"
      aria-label={`Authorization for ${operation}`}
    >
      <div className="api-detail-header">
        <span>Authorizations</span>
        <Select value={scheme.key} onValueChange={setSelected}>
          <SelectTrigger
            aria-label={`Authorization scheme for ${operation}`}
            className="h-8 w-auto min-w-28 max-w-[60%] gap-2 text-xs font-normal"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {schemes.map((item) => (
              <SelectItem key={item.key} value={item.key}>
                {item.key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="api-detail-card api-auth-card">
        <div className="api-detail-heading">
          <code>{scheme.name || "Authorization"}</code>
          <span className="api-field-type">string</span>
          <span className="api-required api-required-badge">required</span>
        </div>
        <p className="api-detail-description">
          {scheme.description ||
            (bearer ? (
              <>
                Authenticate with your {scheme.bearerFormat || "bearer token"}.
              </>
            ) : scheme.type === "apiKey" ? (
              <>
                Send your API key in the {scheme.name} {scheme.in}.
              </>
            ) : (
              <>{scheme.scheme || scheme.type} authentication.</>
            ))}
        </p>
        {bearer && <code className="api-auth-value">Bearer YOUR_TOKEN</code>}
        <p className="mt-3 flex items-center gap-1.5 text-xs leading-5 text-muted">
          <BookOpen size={13} className="shrink-0" aria-hidden />
          <span>
            For setup help, see the{" "}
            <Link
              href="/docs/nexum/authentication"
              className="underline underline-offset-4 focus-visible:outline-brand-primary"
            >
              authentication guide
            </Link>
            .
          </span>
        </p>
      </div>
    </section>
  );
}
